import { Grid, Typography } from '@mui/material';
import {
  SparkTextField,
  SparkDropdown,
  SparkForm,
} from 'sharedComponents/SparkForm';
import { useCountries } from 'hooks/Countries/useCountries';
import { useIndustries } from 'hooks/BusinessIndustries/useIndustries';
import * as yup from 'yup';
import FieldSkeleton from 'sharedComponents/Skeleton/FieldSkeleton';
import Footer from '../Stepper/Footer';

export type PersonalBasicInfoFormValues = {
  name: string;
  countryID: string;
  occupation: string;
  industryID: string;
};

const personalBasicInfoFormValidationSchema = yup.object({
  name: yup.string().required('Name is required'),
  countryID: yup.string().required('Country is required'),
  occupation: yup.string().required('Occupation is required'),
  industryID: yup.string().required('Industry is required'),
});

type PersonalBasicInfoFormProps = {
  shouldRenderWelcomeText?: boolean;
  activeStep: number;
  nextStepHandler: () => void;
  previousStepHandler: () => void;
  personalInfoCollection?: PersonalBasicInfoFormValues;
  setPersonalInfoCollection: (formValues: PersonalBasicInfoFormValues) => void;
};

function PersonalBasicInfoForm({
  shouldRenderWelcomeText = false,
  activeStep,
  nextStepHandler,
  setPersonalInfoCollection,
  previousStepHandler,
  personalInfoCollection,
}: PersonalBasicInfoFormProps) {
  const { dropdownOptions: countries, isLoading: isLoadingCountries } =
    useCountries();
  const { dropdownOptions: industries, isLoading: isLoadingIndustries } =
    useIndustries();
  const isLoading = isLoadingCountries || isLoadingIndustries;

  const personalBasicInfoFormInitialValues = {
    name: personalInfoCollection?.name || '',
    countryID: personalInfoCollection?.countryID || '',
    occupation: personalInfoCollection?.occupation || '',
    industryID: personalInfoCollection?.industryID || '',
  };

  const handleSubmit = (values: PersonalBasicInfoFormValues) => {
    setPersonalInfoCollection(values);
    nextStepHandler();
  };

  if (isLoading) {
    return <FieldSkeleton count={5} />;
  }

  return (
    <SparkForm
      initialValues={personalBasicInfoFormInitialValues}
      validationSchema={personalBasicInfoFormValidationSchema}
      onSave={handleSubmit}
    >
      <Grid item padding={2} paddingLeft={0}>
        <Typography
          fontFamily='Mont-regular'
          justifyContent='flex-start'
          sx={{
            fontSize: '1.0rem',
            color: 'nightblack',
            marginTop: '0.5rem',
          }}
        >
          Tell us more about yourself.
        </Typography>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <SparkTextField
            name='name'
            label='Name'
            size={12}
            placeholder='Enter your Name'
          />
        </Grid>
        <Grid item xs={12}>
          <SparkDropdown
            name='countryID'
            label='Country'
            size={12}
            children={[...countries]}
          />
        </Grid>
        <Grid item xs={12}>
          <SparkTextField
            name='occupation'
            label='Occupation'
            size={12}
            placeholder='Enter your occupation'
          />
        </Grid>
        <Grid item xs={12}>
          <SparkDropdown
            name='industryID'
            label='Industry'
            size={12}
            children={[...industries]}
          />
        </Grid>
      </Grid>

      <Footer
        activeStep={activeStep}
        isLastStep={false}
        handleBack={previousStepHandler}
      />
    </SparkForm>
  );
}

export default PersonalBasicInfoForm;
